/**
 * useNativePush — wires Capacitor's PushNotifications plugin into our
 * existing /api/push/subscribe-native backend on app launch.
 *
 * On WEB this hook is a no-op — the existing Web Push flow
 * (usePushPermission / Notification API) handles browser subscriptions.
 *
 * On NATIVE (iOS/Android) this hook:
 *   1. Requests notification permission (iOS shows the system prompt;
 *      Android 13+ shows it once, older Androids auto-grant).
 *   2. Calls `PushNotifications.register()` to obtain an APNs (iOS) or
 *      FCM (Android) device token.
 *   3. POSTs the token to /api/push/subscribe-native with our
 *      anonymous follower_id so backend can deliver `truck_live` /
 *      `event_inquiry_reply` / etc. notifications to this device.
 *   4. Listens for `pushNotificationActionPerformed` (user taps a
 *      notification) and routes to the deep-link path in `data.url`
 *      via React Router — making the notification → app-screen
 *      handoff feel instant.
 *
 * Init runs ONCE per JS context using a module-level guard. Listeners
 * are added once and NEVER removed — APNs can take 5-30s to issue a
 * token, so any per-mount cleanup risks ripping the listener off before
 * the token event lands. The hook itself can be safely mounted multiple
 * times — only the first one does work.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import api from './api';
// Single source of truth for follower identity — the SAME id the follow
// rows are keyed on. (A previous local copy used a different localStorage
// key, so native push subs never matched the user's follows.)
import { getFollowerId } from './follows';

// Module-level guard — flips true on the FIRST mount, stays true for the
// life of the JS context. Prevents register() / addListener thrash when
// the host component re-mounts (React strict mode, route changes, etc.).
let _initStarted = false;

// Captured at init time so the action-listener can use the latest
// navigate function without forcing useEffect to re-run.
let _navigateRef: ((path: string) => void) | null = null;

async function initNativePush(): Promise<void> {
  try {
    console.warn('[useNativePush] init start — platform:', Capacitor.getPlatform());
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const perm = await PushNotifications.checkPermissions();
    console.warn('[useNativePush] checkPermissions →', JSON.stringify(perm));
    let granted = perm.receive === 'granted';
    if (!granted) {
      console.warn('[useNativePush] requesting permission…');
      const req = await PushNotifications.requestPermissions();
      console.warn('[useNativePush] requestPermissions →', JSON.stringify(req));
      granted = req.receive === 'granted';
    }
    if (!granted) {
      console.warn('[useNativePush] permission NOT granted — bailing');
      return;
    }
    console.warn('[useNativePush] permission granted ✓');

    // Add ALL listeners BEFORE calling register(). Listeners live for
    // the rest of the JS context — never removed. APNs delivery of the
    // device token is async (5-30s), so any cleanup window risks
    // missing the event. Deliberate "leak."
    await PushNotifications.addListener('registration', async (token) => {
      const tokenLen = token.value?.length || 0;
      console.warn('[useNativePush] registration token received, length:', tokenLen);
      try {
        const res = await api.post('/push/subscribe-native', {
          platform: Capacitor.getPlatform(),
          device_token: token.value,
          // Real bundle id from the running app — matches whatever Xcode
          // signed with (e.g. com.thetrucktrackr.app.jlew for dev,
          // com.thetrucktrackr.app for TestFlight).
          bundle_id:
            (window as unknown as { Capacitor?: { getAppId?: () => string } })?.Capacitor?.getAppId?.() ||
            'com.thetrucktrackr.app',
          follower_id: getFollowerId(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          app_version: process.env.REACT_APP_VERSION || null,
        });
        console.warn('[useNativePush] /push/subscribe-native OK', res?.status);
      } catch (e) {
        console.warn('[useNativePush] subscribe-native failed', e);
      }
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('[useNativePush] registration error', JSON.stringify(err));
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      const data = (event.notification && event.notification.data) || {};
      const url: string | undefined = data.url || data.deep_link || data.path;
      const nav = _navigateRef;
      if (!url || !nav) return;
      try {
        const parsed = new URL(url);
        nav(parsed.pathname + parsed.search + parsed.hash);
      } catch {
        nav(url);
      }
    });

    await PushNotifications.addListener('pushNotificationReceived', () => {
      /* no-op — system shows the banner on iOS; future in-app toast goes here */
    });

    console.warn('[useNativePush] calling PushNotifications.register()…');
    await PushNotifications.register();
    console.warn('[useNativePush] register() returned (waiting for token via listener)');
  } catch (e) {
    console.error('[useNativePush] init failed', e);
    // Allow a retry on the next mount if init blew up before listeners
    // were attached.
    _initStarted = false;
  }
}

export function useNativePush() {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  // Always keep the module-level ref pointing at the latest navigate so
  // notification-tap routing uses the current router context.
  _navigateRef = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Android unlocked Aug 2026 — FCM backend is live and the Android
    // project ships google-services.json.
    if (_initStarted) return;
    _initStarted = true;
    void initNativePush();
    // Empty deps — only run once per mount, and the module-level guard
    // ensures only the FIRST mount does any work.
  }, []);
}
